/**
 * LocalStorage Persistence Adapter
 * 
 * Simple persistence adapter that saves wizard drafts to browser localStorage.
 * Perfect for simple wizards that don't need database persistence.
 */

import type { WizardData } from '../types/WizardTypes';
import type { WizardDraft, PersistenceService } from '../stores/useWizardPersistence';

export class LocalStoragePersistenceAdapter<TData extends WizardData> implements PersistenceService<TData> {
  private readonly keyPrefix: string;

  constructor(keyPrefix = 'wizard-draft') {
    this.keyPrefix = keyPrefix;
  }

  async saveDraft(draft: Omit<WizardDraft<TData>, 'id' | 'createdAt' | 'updatedAt'> | WizardDraft<TData>): Promise<string> {
    // Use provided ID if available, otherwise generate one
    const draftId = (draft as WizardDraft<TData>).id || `${this.keyPrefix}-${draft.wizardId}-${Date.now()}`;
    const now = new Date();
    
    const fullDraft: WizardDraft<TData> = {
      ...draft,
      id: draftId,
      createdAt: (draft as WizardDraft<TData>).createdAt || now,
      updatedAt: now,
    };

    try {
      localStorage.setItem(draftId, JSON.stringify(fullDraft));
      return draftId;
    } catch (error) {
      console.error('Failed to save draft to localStorage:', error);
      throw new Error('Failed to save draft');
    }
  }

  async loadDraft(draftId: string): Promise<WizardDraft<TData> | null> {
    try {
      const draftJson = localStorage.getItem(draftId);
      if (!draftJson) {
        return null;
      }

      const draft = JSON.parse(draftJson) as WizardDraft<TData>;
      
      // Convert date strings back to Date objects
      draft.createdAt = new Date(draft.createdAt);
      draft.updatedAt = new Date(draft.updatedAt);
      
      return draft;
    } catch (error) {
      console.error('Failed to load draft from localStorage:', error);
      return null;
    }
  }

  async deleteDraft(draftId: string): Promise<boolean> {
    try {
      localStorage.removeItem(draftId);
      return true;
    } catch (error) {
      console.error('Failed to delete draft from localStorage:', error);
      return false;
    }
  }

  async listDrafts(wizardId: string): Promise<WizardDraft<TData>[]> {
    try {
      // With single draft per wizard, just check if the draft exists
      const draftId = `${this.keyPrefix}-${wizardId}`;
      const draft = await this.loadDraft(draftId);
      return draft ? [draft] : [];
    } catch (error) {
      console.error('Failed to list drafts from localStorage:', error);
      return [];
    }
  }

  /**
   * Clear the single draft for a specific wizard (useful for cleanup)
   */
  async clearWizardDrafts(wizardId: string): Promise<void> {
    try {
      const draftId = `${this.keyPrefix}-${wizardId}`;
      localStorage.removeItem(draftId);
    } catch (error) {
      console.error('Failed to clear wizard drafts:', error);
    }
  }

  /**
   * Get storage usage statistics
   */
  getStorageStats(): { totalDrafts: number; estimatedSize: string } {
    let totalDrafts = 0;
    let totalSize = 0;

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.keyPrefix)) {
        totalDrafts++;
        totalSize += key.length + (localStorage.getItem(key)?.length || 0);
      }
    }

    const sizeInKB = (totalSize / 1024).toFixed(2);
    return {
      totalDrafts,
      estimatedSize: `${sizeInKB} KB`,
    };
  }
}
